import { Injectable, Inject, InjectionToken } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for user interaction API path
 */
export const USER_INTERACTION_API_PATH = new InjectionToken<string>('USER_INTERACTION_API_PATH');

/**
 * User Interaction API Service for handling user interaction-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class UserInteractionApiService {
  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(USER_INTERACTION_API_PATH) private userInteractionApiPath: string,
  ) {}

  /**
   * Add a bookmark for a user
   * @param payload Bookmark data (e.g., { itemId: string, itemType: string })
   * @returns Promise with bookmark response
   */
  async addBookmark(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}bookmark`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error adding bookmark:', error);
      throw error;
    }
  }

  /**
   * Remove a bookmark for a user
   * @param payload Bookmark data (e.g., { itemId: string, itemType: string })
   * @returns Promise with bookmark removal response
   */
  async removeBookmark(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}bookmark`;

      const response = await this.networkService.delete_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error removing bookmark:', error);
      throw error;
    }
  }

  /**
   * Get bookmarks for a user
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with bookmarks list
   */
  async getBookmarks(page: number = 1, size: number = 10): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}bookmark?page=${page}&size=${size}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      throw error;
    }
  }

  /**
   * Add a like for an item
   * @param payload Like data (e.g., { itemId: string, itemType: string })
   * @returns Promise with like response
   */
  async addLike(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}like`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error adding like:', error);
      throw error;
    }
  }

  /**
   * Remove a like for an item
   * @param payload Like data (e.g., { itemId: string, itemType: string })
   * @returns Promise with like removal response
   */
  async removeLike(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}like`;

      const response = await this.networkService.delete_api(
        url,
        payload,
        undefined, // headers - not needed, interceptors handle Content-Type and Authorization
      );

      return response;
    } catch (error) {
      console.error('Error removing like:', error);
      throw error;
    }
  }

  /**
   * Get likes for an item
   * @param itemId Item ID
   * @param itemType Item type (e.g., 'dataset', 'model', 'usecase')
   * @returns Promise with likes data
   */
  async getLikes(itemId: string, itemType: string): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}like?itemId=${itemId}&itemType=${itemType}`;

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching likes:', error);
      throw error;
    }
  }

  /**
   * Get user interactions
   * @param queryParams Query parameters object (e.g., { page: 1, size: 10, type: 'bookmark' })
   * @returns Promise with user interactions data
   */
  async userInteractions(queryParams?: Record<string, any>): Promise<any> {
    try {
        // https://v2.dev.controlplane.iudx.io/iudx/v2/user/interactions
      let url = `${this.basePathUrl}${this.userInteractionApiPath}/interactions`;

      // Merge with default pagination: size=6 (override if caller provides size)
      const mergedParams: Record<string, unknown> = { size: 6, ...queryParams };

      // Build query string from merged params
      if (mergedParams && Object.keys(mergedParams).length > 0) {
        const params = new URLSearchParams();
        Object.keys(mergedParams).forEach((key) => {
          const value = mergedParams[key];
          if (value !== null && value !== undefined && value !== '') {
            params.append(key, String(value));
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const response = await this.networkService.get_api(
        url,
        undefined,
        false, // disableToaster
      );

      return response;
    } catch (error) {
      console.error('Error fetching user interactions:', error);
      throw error;
    }
  }

  /**
   * Create a bookmark or like for an item
   * @param payload Interaction data (e.g., { itemId: string, itemType: string, type: 'bookmark' | 'like' })
   * @returns Promise with interaction response
   */
  async createBookmarkOrLike(payload: any): Promise<any> {
    try {
      const url = `${this.basePathUrl}${this.userInteractionApiPath}/interactions`;
      const headers = {
        'Content-Type': 'application/json',
      };

      const response = await this.networkService.post_api(
        url,
        payload,
        headers,
        true, // isToasterRequired
        true, // isLoaderRequired
      );

      return response;
    } catch (error) {
      console.error('Error creating bookmark or like:', error);
      throw error;
    }
  }
}
